import { useMemo } from 'react';

import { ChecklistStep, ChecklistSubstep } from './DashboardChecklist.interface';
import {
    ACCOUNTING_SETUP_TASK_ID,
    ACCOUNTING_SUBSTEP_COPY,
    CONTINUE_CTA_LABEL,
    DEFAULT_CTA_LABEL,
    STEP_COPY,
} from './DashboardChecklist.constants';

import { LaunchpadItem, TaskStatus } from 'modules/common/components/LaunchpadProgress/LaunchpadProgress.interface';
import {
    getLaunchpadCtaLabel,
    getNextLaunchpadTask,
    isLaunchpadItemBlocked,
    isLaunchpadItemCancelled,
    isLaunchpadItemCompleted,
} from 'modules/common/components/LaunchpadProgress/launchpadItemState';
import { useMember } from 'modules/common/queries/member';
import { shouldShowImportTransactionsStep } from 'modules/Onboarding/AccountingSetup';
import {
    OnboardingProgress,
    useAccountingOnboardingProgress,
} from 'services/bookkeeping/queries/useAccountingOnboardingProgress';
import { isSCorpMember } from 'utils/businessStructure';

const COMPLETED = 'completed';

// Same applicability rules as the accounting setup wizard's steps.
const getAccountingSubsteps = (
    progress: OnboardingProgress | undefined,
    usertype: string | undefined
): ChecklistSubstep[] => {
    const substeps: ChecklistSubstep[] = [];
    if (shouldShowImportTransactionsStep(progress)) {
        substeps.push({
            ...ACCOUNTING_SUBSTEP_COPY.personalAccounts,
            completed: progress?.transaction_import_status === COMPLETED,
        });
    }
    if (isSCorpMember(usertype || '')) {
        substeps.push({
            ...ACCOUNTING_SUBSTEP_COPY.reimbursements,
            completed: progress?.home_office_setup_status === COMPLETED,
        });
    }
    substeps.push({
        ...ACCOUNTING_SUBSTEP_COPY.documents,
        completed: progress?.other_document_upload_status === COMPLETED,
    });
    return substeps;
};

export const buildChecklistSteps = (
    items: LaunchpadItem[],
    progress: OnboardingProgress | undefined,
    usertype: string | undefined
): ChecklistStep[] =>
    items
        .filter((item) => !isLaunchpadItemCancelled(item))
        .map((item) => {
            const copy = STEP_COPY[item.template_id];
            const completed = isLaunchpadItemCompleted(item);
            const isBlocked = isLaunchpadItemBlocked(item, items);
            const nextTask = getNextLaunchpadTask(item);
            const substeps =
                item.template_id === ACCOUNTING_SETUP_TASK_ID
                    ? getAccountingSubsteps(progress, usertype)
                    : item.subtasks.map((subtask) => ({
                          id: subtask.id,
                          title: subtask.name,
                          description: subtask.description,
                          completed: subtask.status === TaskStatus.COMPLETED,
                      }));
            // A finished substep means the step is underway even if its task was never marked in progress.
            const fallbackLabel = substeps.some((substep) => substep.completed)
                ? CONTINUE_CTA_LABEL
                : copy?.ctaLabel ?? DEFAULT_CTA_LABEL;

            return {
                id: item.id,
                title: copy?.title ?? item.name,
                description: copy?.description ?? item.description,
                completed,
                isReady: !completed && !isBlocked && !!nextTask?.cta_url,
                ctaLabel: getLaunchpadCtaLabel(nextTask, item, isBlocked, fallbackLabel),
                nextTask,
                substeps,
            };
        });

function useDashboardChecklistSteps(items: LaunchpadItem[]): ChecklistStep[] {
    const hasOpenAccountingStep = items.some(
        (item) => item.template_id === ACCOUNTING_SETUP_TASK_ID && !isLaunchpadItemCompleted(item)
    );
    const { data: progress } = useAccountingOnboardingProgress({ enabled: hasOpenAccountingStep });
    const { data: member } = useMember();

    return useMemo(() => buildChecklistSteps(items, progress, member?.usertype), [items, progress, member?.usertype]);
}

export default useDashboardChecklistSteps;
